import React, {useContext, useEffect, useState} from "react";
import {CampaignBigType, DeliveryStatus} from "./dataForActiveCampaign";
import {getAutoCampaignDetail, getCampaignDetail} from "./campaignService";
import Spinner from "../../components/spinner/spinner";
import {CampaignStatisticDetail, DefaultCampaignStatisticDetail} from "./dataForCampaignStatistic";
import Button from "../../components/button/button";
import {ButtonStatus, ButtonType} from "../../components/button/enum";
import PageButton from "../../components/pageButton/pageButton";
import {getFile} from "../../util/http/HTTP";
import {ApiCampaign} from "../../util/http/API";
import {formatPhone} from "../../util/util";
import ToolTips, {ToolTipPosition} from "../../components/toolTips/toolTips";
import DropdownListForm, {DropdownListItem} from "../../components/forms/dropdownListForm";
import {GrayLog} from "../../grayLog/grayLog";
import {LogEventName} from "../../grayLog/logKey";
import {PromptContext} from "../promptContext";

const pageSize = 10

interface IProps {
    campaignBigType: CampaignBigType,
    fileName: string,
    campaignId: string,
    relationId: string,
    patientCount: number,
    sendSuccessCount: number,
    sendFailedCount: number,
    tobeSentCount: number,
    onBack: () => void
}

export enum MessageStatus {
    success,
    fail,
    inProgress,
    all = 99,
}

export enum DateFilterKey {
    today,
    last7Days,
    last30Days,
    all,
}

const dataForDeliverDateDropdown: Array<DropdownListItem> = [
    {name: "Today", id: DateFilterKey.today},
    {name: "Last 7 days", id: DateFilterKey.last7Days},
    {name: "Last 30 days", id: DateFilterKey.last30Days},
    {name: "All", id: DateFilterKey.all},
]

export interface MessageFilter {
    msgStatus: MessageStatus,
    dateFilter: DateFilterKey,
}

function CampaignStatisticList(props: IProps) {
    const {
        campaignBigType,
        fileName,
        campaignId,
        relationId,
        // patientCount,
        onBack,
    } = props

    const [page, setPage] = useState(1)
    const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false)
    const defaultMsgFilter = {msgStatus: MessageStatus.all, dateFilter: DateFilterKey.today}
    const [messageFilter, setMessageFilter] = useState<MessageFilter>(defaultMsgFilter)
    const [totalCount, setTotalCount] = useState<number>(0)
    const [campaignDetail, setCampaignDetail] = useState<CampaignStatisticDetail>(DefaultCampaignStatisticDetail)

    const {showError} = useContext(PromptContext)

    const {list, successCount, failCount, pendingCount} = campaignDetail

    useEffect(() => {
        reloadData()
    }, [page])

    useEffect(() => {
        if (page === 1 && messageFilter !== defaultMsgFilter) {
            reloadData()
        } else {
            setPage(1)
        }
    }, [messageFilter])

    const reloadData = () => {
        if (campaignBigType === CampaignBigType.regularCampaign) {
            getRegularCampaignDetailByPage()
        } else if (campaignBigType === CampaignBigType.automatedCampaign) {
            getAutomatedCampaignDetailByPage()
        }
    }

    const getRegularCampaignDetailByPage = () => {
        setIsShowSpinner(true)
        getCampaignDetail(campaignId, messageFilter, page, pageSize, (detail, total) => {
            setCampaignDetail(detail)
            setTotalCount(total)
            setIsShowSpinner(false)
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth",
            });
        }, (error) => {
            setIsShowSpinner(false)
            showError(error)
        })
    }

    const getAutomatedCampaignDetailByPage = () => {
        setIsShowSpinner(true)
        getAutoCampaignDetail(relationId, messageFilter, page, pageSize, (detail, total) => {
            setCampaignDetail(detail)
            setTotalCount(total)
            setIsShowSpinner(false)
            window.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth",
            });
        }, (error) => {
            setIsShowSpinner(false)
            showError(error)
        })
    }

    const startPageNumber = ((page - 1) * pageSize + 1)
    const endPageNumber = (page) * pageSize >= totalCount ? totalCount : (page) * pageSize
    const pageRange = `${startPageNumber}-${endPageNumber}`
    const $pageIndex = totalCount ? (
        <div className={"flex flex-row items-center text-sm text-gray-1 space-x-2"}>
            <span className={"text-black font-bold"}>{pageRange}</span>
            <span className={"text-gray-2"}>of</span>
            <span className={"text-gray-3"}>{totalCount}</span>
        </div>) : null

    const onNextPage = () => {
        if (page * pageSize >= totalCount) {
            return
        }
        setPage(page + 1)
    }
    const onPreviousPage = () => {
        if (page > 1) {
            setPage(page - 1)
        }
    }

    const hasPreviousPage = (page > 1)
    const hasNextPage = !(page * pageSize > totalCount)
    const $pageButton = (
        <div className={"flex flex-row items-center justify-center space-x-4"}>
            <PageButton enable={hasPreviousPage} rotate={"rotate-180"} onClick={onPreviousPage} />
            <PageButton enable={hasNextPage} onClick={onNextPage} />
        </div>
    )

    const exportCampaignStatistic = () => {
        if (campaignBigType === CampaignBigType.regularCampaign) {
            const param = {
                fileType: 1,
                campaignId: campaignId,
            }
            getFile(ApiCampaign.getFileByCampaign, fileName, param)
        } else if (campaignBigType === CampaignBigType.automatedCampaign) {
            const param = {
                fileType: 1,
                relationId: relationId,
            }
            getFile(ApiCampaign.getFileByAutoCampaign, fileName, param)
        }
    }
    const $exportCsvButton = (
        <ToolTips disable={list.length > 0} position={ToolTipPosition.top} tips={"No patient list to export"}>
            <Button type={ButtonType.outline} status={list.length ? ButtonStatus.primary : ButtonStatus.disabled} onClick={() => {
                exportCampaignStatistic()
            }} >
                <span className={"text-sm"}>Export Patient List to CSV</span>
            </Button>
        </ToolTips>
    )

    const $tableFooter = (
        <div className={"w-full flex flex-row items-center justify-between mt-6"}>
            {$exportCsvButton}
            <div className={"flex flex-row items-center space-x-20"}>
                {$pageIndex}
                {$pageButton}
            </div>
        </div>
    )

    const $row = (value: string | number, isHighlight?: boolean) => {
        return <p className={`${isHighlight ? "text-gray-1" : "text-gray-2"} text-sm font-normal`}>{value}</p>
    }

    const getStatusName = (status: DeliveryStatus): [string, string, string] => {
        if (status === DeliveryStatus.created || status === DeliveryStatus.sending) {
            return ["In Progress", "text-gray-3", "bg-gray-5"]
        } else if (status === DeliveryStatus.failed || status === DeliveryStatus.reject) {
            return ["Failed", "text-red-500", "bg-red-30"]
        } else if (status === DeliveryStatus.success) {
            return ["Success", "text-green-1", "bg-green-1-30"]
        }
        return ["Unknown", "text-gray-3", "bg-gray-5"]
    }

    const $statusRow = (status: DeliveryStatus, phone: string) => {
        const [, statusTextColor, statusTextBgColor] = getStatusName(status)
        return (
            <div className={`py-1 px-1.5 rounded ${phone ? statusTextBgColor : "bg-orange-30"} w-25`}>
                <p className={`text-xs ${phone ? statusTextColor : "text-orange"} text-center`}>{phone ? formatPhone(phone) : "xxx-xxx-xxxx"}</p>
            </div>
        )
    }

    const $activeCampaignContent = () => {
        return list.map((campaign, idx) => {
            return (
                <tr className={"border-b border-border-black-12"} key={idx}>
                    <td className={"py-3"}>{$row(campaign.name, true)}</td>
                    <td className={"py-3"}>{$row(campaign.dob)}</td>
                    <td className={"py-3"}>{$row(campaign.gender)}</td>
                    <td className={"py-3"}>{$row(campaign.email)}</td>
                    {campaignBigType !== CampaignBigType.automatedCampaign ? <td className={"py-3"}>{$row(campaign.lastVisit)}</td> : null}
                    <td className={"py-3"}>{$statusRow(campaign.deliveryStatus, campaign.phone)}</td>
                    <td className={"py-3"}>{$row(campaign.deliverTime)}</td>
                </tr>
            )
        })
    }

    const $title = <p className={"text-lg text-gray-1 font-bold"}>Campaign Statistics</p>
    const $closeButton = <Button status={ButtonStatus.float} onClick={() => {
        onBack && onBack()
    }} >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 4.5L14.0806 14.0806" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4.5 14.0811L14.0806 4.50041" stroke="#14142B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    </Button>

    const sendSuccessRate = successCount && totalCount ? ((successCount / totalCount) * 100).toFixed(successCount === totalCount ? 0 : 1) : "0"
    const $statisticStatusHeader = campaignBigType !== CampaignBigType.automatedCampaign ? (<div>
        <p className={"text-sm text-gray-2 mt-4"}>{`${totalCount} Patients, ${sendSuccessRate}% Delivered`}</p>
    </div>) : null

    const onChangeMsgStatus = (preStatus: MessageStatus) => {
        setMessageFilter({
            ...messageFilter,
            msgStatus: messageFilter.msgStatus === preStatus ? MessageStatus.all : preStatus,
        })
    }

    const $rowForSentSuccess = (
        <button
            className={`p-2 text-xs text-green-1 rounded font-semibold bg-green-1-30 max-w-max ${messageFilter.msgStatus === MessageStatus.success ? "ring-1 ring-green-1" : ""}`}
            onClick={() => {
                onChangeMsgStatus(MessageStatus.success)
            }}>
            {`${successCount === undefined ? "" : successCount} SMS Successfully Sent`}
        </button>
    )
    const $rowForSentFailed = (
        <button
            className={`p-2 rounded text-xs font-semibold text-red-500 bg-red-30 max-w-max ${messageFilter.msgStatus === MessageStatus.fail ? "ring-1 ring-red-500" : ""}`}
            onClick={() => {
                onChangeMsgStatus(MessageStatus.fail)
            }}>
            {`${failCount === undefined ? "" : failCount} SMS Failed`}
        </button>
    )
    const $rowForWillBeSent = (
        <button
            className={`p-2 rounded text-gray-3 text-xs font-semibold bg-gray-5 max-w-max ${messageFilter.msgStatus === MessageStatus.inProgress ? "ring-1 ring-gray-3" : ""}`}
            onClick={() => {
                onChangeMsgStatus(MessageStatus.inProgress)
            }}>
            {`${pendingCount === undefined ? "" : pendingCount} SMS To Be Delivered`}
        </button>
    )
    // const $rowForMissingPhone = (
    //     <button
    //         className={`p-2 rounded text-orange text-xs font-semibold bg-orange-30 max-w-max ${messageFilter.msgStatus === MessageStatus.missingPhone ? "ring-1 ring-orange" : ""}`}
    //         onClick={() => {
    //             onChangeMsgStatus(MessageStatus.missingPhone)
    //         }}>
    //         {"Missing Phone Number"}
    //     </button>
    // )

    const onChangeDateFilter = (id: DateFilterKey) => {
        if (id !== messageFilter.dateFilter) {
            setMessageFilter({
                ...messageFilter,
                dateFilter: id,
            })

            const dateFilterKey = id
            GrayLog.event(LogEventName.changeDeliverDateRange, {deliverDateRange: dataForDeliverDateDropdown.find(({id}) => id === dateFilterKey)?.name})
        }
    }

    const $statusHeader = (
        <div className={"w-full flex flex-row items-center mt-3"}>
            <div className={"w-full flex flex-row items-center space-x-2"}>
                {$rowForSentSuccess}
                {$rowForSentFailed}
                {$rowForWillBeSent}
            </div>
            <div className={"w-40"}>
                <DropdownListForm id={messageFilter.dateFilter} data={dataForDeliverDateDropdown} onChange={onChangeDateFilter}/>
            </div>
        </div>
    )

    const $header = (
        <div className={"w-full text-left"}>
            <div className={"flex flex-row items-center justify-between"}>
                {$title}
                {$closeButton}
            </div>
            {$statisticStatusHeader}
            {$statusHeader}
        </div>
    )

    const $tableHeader = (
        <tr className={"text-left text-gray-2 font-bold border-b border-base-border text-sm"}>
            <th className={"pb-4 text-sm"}>Patient</th>
            <th className={"pb-4 text-sm"}>DOB</th>
            <th className={"pb-4 text-sm"}>Gender</th>
            <th className={"pb-4 text-sm"}>Email</th>
            {campaignBigType !== CampaignBigType.automatedCampaign ? <th className={"pb-4 text-sm"}>Last Visit</th> : null}
            <th className={"pb-4 text-sm"}>Deliver Status</th>
            <th className={"pb-4 text-sm"}>Deliver Time</th>
        </tr>
    )

    const $table = (
        <table className={"w-full mt-4"}>
            <thead>
                {$tableHeader}
            </thead>
            <tbody>
                {$activeCampaignContent()}
            </tbody>
        </table>
    )

    return (
        <div className={"w-256"}>
            <div className={"p-2 flex flex-col items-center bg-white rounded"}>
                {$header}
                {$table}
                {$tableFooter}
            </div>
            <Spinner isOpen={isShowSpinner} />
        </div>
    )
}

export default CampaignStatisticList
